import { IOrderBy } from 'api/odata.types'
import { trim } from 'lodash'
import { flow } from 'lodash/fp'
import { all, call, delay, put, select, takeLatest } from 'typed-redux-saga'
import { ActionType, createAction, createReducer } from 'typesafe-actions'
import { IAccount } from '../../api/account.types'
import { IClient } from '../../api/client.types'
import { ISearchParams, ISearchResult } from '../../api/common.types'
import { notNullOrEmpty } from '../../shared'
import { AppState } from '../../store'
import { accountContextSelectionActions } from '../../store/context/account'
import { clientContextUpdateActions } from '../../store/context/client'
import { safeSearch } from '../../store/shared/sagas'
import { ICombinedSearchResults } from './shared/ICombinedSearchResults'

const UPDATE_SEARCH_TEXT = '@features/@search/UPDATE_SEARCH_TEXT'

export const searchBoxActions = {
  updateSearchText: createAction(UPDATE_SEARCH_TEXT)<string | undefined>()
}

const REQUESTED = '@features/@search/REQUESTED'
const SUCCESS = '@features/@search/SUCCESS'
const FAILURE = '@features/@search/FAILURE'

export const searchActions = {
  request: createAction(REQUESTED)<string>(),
  success: createAction(SUCCESS)<ICombinedSearchResults>(),
  failure: createAction(FAILURE)<Error>()
}

const CLIENT_SELECTED = '@features/@search/CLIENT_SELECTED'
const ACCOUNT_SELECTED = '@features/@search/ACCOUNT_SELECTED'

export const searchResultActions = {
  clientSelected: createAction(CLIENT_SELECTED)<IClient>(),
  accountSelected: createAction(ACCOUNT_SELECTED)<IAccount>()
}

export type HeaderActionTypes =
  | ActionType<typeof searchBoxActions>
  | ActionType<typeof searchActions>

export interface ISearchState {
  searchText?: string
  searchLoading: boolean
  searchResults?: ICombinedSearchResults
  loadedSearchText?: string
}

const initialState: ISearchState = {
  searchLoading: false
}

export const searchReducer = createReducer<ISearchState, HeaderActionTypes>(
  initialState
)
  .handleAction(searchBoxActions.updateSearchText, (state, action) => {
    return action.payload === undefined
      ? { ...initialState }
      : {
          ...state,
          searchText: action.payload
        }
  })
  .handleAction(searchActions.request, (state) => {
    return {
      ...state,
      searchLoading: true
    }
  })
  .handleAction(searchActions.success, (state, action) => {
    return {
      ...state,
      searchResults: action.payload,
      searchLoading: false,
      loadedSearchText: state.searchText
    }
  })

const rootSelector = (state: AppState) => state.features.search
export const getIsSearchLoading = flow(rootSelector, (x) => x.searchLoading)
export const getSearchText = flow(rootSelector, (x) => x.searchText)
export const getLoadedSearchText = flow(rootSelector, (x) => x.loadedSearchText)
export const getSearchResults = flow(rootSelector, (x) => x.searchResults)
export const searchSelectors = {
  getIsSearchLoading,
  getLoadedSearchText,
  getSearchResults,
  getSearchText
}

const fetchSearchResults = function* (query: string) {
  yield delay(300)

  query = (query || '').trim()

  try {
    const params: ISearchParams = {
      top: 5,
      count: true,
      query
    }

    const [clients, accounts] = yield* all([
      call(safeSearch, 'household' as const, {
        ...params,
        searchFields: ['householdName', 'Account', 'Parties/LegalEntityName'],
        select: [
          'Account',
          'noOfAccounts',
          'householdKPI',
          'Parties',
          'Advisors',
          'householdName',
          'householdId',
          'id',
          'RockConnectAccBalance'
        ],
        orderBy: [
          { dataPath: 'search.score()', direction: 'desc' },
          { dataPath: 'RockConnectAccBalance', direction: 'desc' },
          { dataPath: 'householdKPI/AumTotal', direction: 'desc' }
        ] as IOrderBy[]
      }),
      call(safeSearch, 'client' as const, {
        ...params,
        searchFields: ['LegalEntityName', 'srcClientNumber', 'Account'],
        select: [
          'ClientAdvisorID',
          'ClientAdvisor',
          'ClientAdvisorTeam',
          'LegalEntityName',
          'LegalEntityID',
          'legalEntityType',
          'ClientKPI/AumTotal',
          'loginDetails',
          'Account',
          'srcClientNumber',
          'id',
          'householdId',
          'RockConnectAccBalance'
        ],
        orderBy: [
          { dataPath: 'search.score()', direction: 'desc' },
          { dataPath: 'RockConnectAccBalance', direction: 'desc' },
          { dataPath: 'ClientKPI/AumTotal', direction: 'desc' }
        ] as IOrderBy[]
      }),
      call(safeSearch, 'account' as const, {
        ...params,
        searchFields: [
          'CustodyAccount',
          'AdvisorAddedNickName',
          'LegalEntityName',
          'householdName',
          'gfoCustodyAccount'
        ],
        select: [
          'ClientAdvisorID',
          'ClientAdvisor',
          'ClientAdvisorTeam',
          'CustodyAccount',
          'LegalEntityName',
          'LegalEntityID',
          'AccountKPIs/AccountTotal',
          'AdvisorAddedNickName',
          'Shortname',
          'CustodianType',
          'CustodianName',
          'registrationtype',
          'gfoCustodyAccount',
          'id',
          'registrationDesc',
          'RockConnectAccBalance',
          'accountStatus'
        ],
        orderBy: [
          { dataPath: 'search.score()', direction: 'desc' },
          { dataPath: 'RockConnectAccBalance', direction: 'desc' },
          { dataPath: 'AccountKPIs/AccountTotal', direction: 'desc' }
        ] as IOrderBy[]
      })
    ])

    yield put(
      searchActions.success({
        clients: clients as ISearchResult<IClient>,
        accounts: accounts as ISearchResult<IAccount>
      })
    )
  } catch (e: any) {
    yield put(searchActions.failure(e))
  }
}

export const searchSagas = [
  () =>
    takeLatest(
      searchBoxActions.updateSearchText,
      function* (action: ReturnType<typeof searchBoxActions.updateSearchText>) {
        if (action.payload === undefined) {
          return
        }
        const loadedSearchText: string | undefined = yield select(
          getLoadedSearchText
        )

        if (
          loadedSearchText &&
          trim(loadedSearchText) === trim(action.payload)
        ) {
          return
        }

        yield put(searchActions.request(action.payload))
      }
    ),
  () =>
    takeLatest(
      searchActions.request,
      function* (action: ReturnType<typeof searchActions.request>) {
        yield call(fetchSearchResults, action.payload)
      }
    ),
  () =>
    takeLatest(
      searchResultActions.accountSelected,
      function* (
        action: ReturnType<typeof searchResultActions.accountSelected>
      ) {
        const { ClientAdvisorID, LegalEntityID, id } = action.payload

        if (LegalEntityID) {
          yield put(
            clientContextUpdateActions.request({
              legalEntityIds: [LegalEntityID],
              clientAdvisorId: ClientAdvisorID
            })
          )
        }

        if (id) {
          yield put(accountContextSelectionActions.update([id]))
        }
      }
    ),
  () =>
    takeLatest(
      searchResultActions.clientSelected,
      function* (
        action: ReturnType<typeof searchResultActions.clientSelected>
      ) {
        if (!action.payload) {
          return
        }

        const { LegalEntityID, ClientAdvisorID } = action.payload

        yield put(
          clientContextUpdateActions.request({
            legalEntityIds: [LegalEntityID].filter(notNullOrEmpty),
            clientAdvisorId: ClientAdvisorID
          })
        )
      }
    )
]
